import type { ReactElement, ReactNode } from "react";
import { Suspense } from "react";
import { Box, MenuItem, TextField, useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material";
import { useLocation, useNavigate } from "react-router";
import { Tab2 } from "./Tab2";
import { Tabs2 } from "./Tabs2";
import { LoadingIndicator } from "./LoadingIndicator";

export type ResponsiveHashTab = {
  value: string;
  oldValue?: string;
  label: string | ReactElement;
  mobileLabel?: string;
  badge?: ReactNode;
  content: ReactNode;
};

export function ResponsiveHashTabs(props: {
  tabs: ResponsiveHashTab[];
  mobileSelectAriaLabel: string;
  className?: string;
}) {
  const { tabs, mobileSelectAriaLabel, className } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const selectedTab = getSelectedResponsiveHashTab(tabs, location.hash);

  if (isMobile) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxWidth: "100%", minWidth: 0, width: "100%" }}>
        <TextField
          select
          value={selectedTab.value}
          inputProps={{ "aria-label": mobileSelectAriaLabel }}
          SelectProps={{
            displayEmpty: true,
            renderValue: (value) => _getMobileLabel(tabs.find((tab) => tab.value === value) ?? tabs[0]),
          }}
          onChange={(event) => {
            const hash = event.target.value ? `#${event.target.value}` : "";
            navigate({ pathname: location.pathname, search: location.search, hash });
          }}
          size="small"
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "999px",
              overflow: "hidden",
              "& .MuiOutlinedInput-notchedOutline": {
                borderRadius: "inherit",
              },
            },
            "& .MuiSelect-select": {
              backgroundColor: "transparent",
            },
          }}
        >
          {tabs.map((tab) => (
            <MenuItem key={tab.value || "default"} value={tab.value}>
              {_getMobileLabel(tab)}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ maxWidth: "100%", minWidth: 0, overflowX: "hidden", width: "100%" }}>
          <Suspense fallback={<LoadingIndicator />}>{selectedTab.content}</Suspense>
        </Box>
      </Box>
    );
  }

  return (
    <Tabs2 className={className} syncWith="hash">
      {tabs.map((tab) => (
        <Tab2
          key={tab.value || "default"}
          value={tab.value}
          oldValue={tab.oldValue}
          label={tab.label}
          badge={tab.badge}
        >
          <Suspense fallback={<LoadingIndicator />}>{tab.content}</Suspense>
        </Tab2>
      ))}
    </Tabs2>
  );
}

function _getMobileLabel(tab: ResponsiveHashTab): string {
  return tab.mobileLabel ?? (typeof tab.label === "string" ? tab.label : tab.value);
}

export function getSelectedResponsiveHashTab<T extends { value: string; oldValue?: string }>(
  tabs: T[],
  hash: string,
): T {
  const activeTabValue = hash.startsWith("#") ? hash.slice(1).split("&")[0] : "";
  const selectedTab = tabs.find((tab) => tab.value === activeTabValue || tab.oldValue === activeTabValue) ?? tabs[0];
  if (!selectedTab) {
    throw new Error("Expected at least one responsive hash tab.");
  }
  return selectedTab;
}
